import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Shop the look — lets a shopper pick one variant per product from a curated look
 * and add the whole selection to the cart in a single request.
 *
 * Selections are keyed by product id so a product can only be added once; changing the
 * size <select> updates the matching checkbox's variant + price in place.
 *
 * @typedef {object} ShopTheLookRefs
 * @property {HTMLButtonElement} submit - The footer add-to-cart button.
 * @extends Component<ShopTheLookRefs>
 */
class ShopTheLookComponent extends Component {
  requiredRefs = ['submit'];

  /** @type {Map<string, { variantId: string, price: number }>} */
  #selections = new Map();

  get #moneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  get #currency() {
    return this.dataset.currency || 'USD';
  }

  get #addLabel() {
    return this.dataset.addLabel || 'Add to cart';
  }

  /**
   * Syncs the checkbox (price, label, variant id) to the variant chosen in the item's size <select>.
   * @param {Event} event
   */
  handleVariantChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    const item = /** @type {HTMLElement | null} */ (select.closest('.shop-look__item'));
    const option = select.options[select.selectedIndex];
    if (!item || !option) return;

    const price = Number(option.dataset.price) || 0;
    item.dataset.price = String(price);

    const input = this.#inputFor(item);
    if (input) input.value = select.value;

    const priceDisplay = item.querySelector('[data-price-display]');
    if (priceDisplay) priceDisplay.textContent = this.#money(price);

    const label = item.querySelector('.checkbox__label-text');
    if (label) label.textContent = `${this.#addLabel} - ${this.#money(price)}`;

    // Keep an already-selected item's stored variant/price up to date.
    if (input?.checked) this.#syncSelection(item, input);
  }

  /**
   * The native checkbox snippet binds the event to its `.checkbox` wrapper, so the
   * proxied target is the wrapper — resolve the item and its input from there.
   * @param {Event} event
   */
  handleSelect(event) {
    const wrapper = /** @type {HTMLElement} */ (event.target);
    const item = /** @type {HTMLElement | null} */ (wrapper.closest('.shop-look__item'));
    if (!item) return;

    const input = this.#inputFor(item);
    if (input) this.#syncSelection(item, input);
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLInputElement | null}
   */
  #inputFor(item) {
    return /** @type {HTMLInputElement | null} */ (item.querySelector('.checkbox__input'));
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLInputElement} input
   */
  #syncSelection(item, input) {
    const productId = item.dataset.productId;
    if (!productId) return;

    if (input.checked) {
      this.#selections.set(productId, {
        variantId: input.value,
        price: Number(item.dataset.price) || 0,
      });
    } else {
      this.#selections.delete(productId);
    }

    this.#updateSubmit();
  }

  #updateSubmit() {
    const { submit } = this.refs;

    if (this.#selections.size === 0) {
      submit.disabled = true;
      submit.textContent = this.dataset.emptyLabel || this.#addLabel;
      return;
    }

    const total = [...this.#selections.values()].reduce((sum, selection) => sum + selection.price, 0);
    submit.disabled = false;
    submit.textContent = `${this.#addLabel} - ${this.#money(total)}`;
  }

  /** @param {number} price */
  #money(price) {
    return formatMoney(price, this.#moneyFormat, this.#currency);
  }

  /** Adds every selected variant to the cart, then refreshes + opens the cart drawer. */
  async handleSubmit() {
    if (this.#selections.size === 0) return;

    const { submit } = this.refs;
    submit.disabled = true;

    const sectionIds = new Set();
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.add(element.dataset.sectionId);
    }

    const payload = {
      items: [...this.#selections.values()].map((selection) => ({
        id: Number(selection.variantId),
        quantity: 1,
      })),
      sections: [...sectionIds].join(','),
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      }).then((res) => res.json());

      if (response.status) {
        // Server-side error (e.g. sold out) — re-enable so the shopper can retry.
        submit.disabled = false;
        return;
      }

      // Notifies cart-items (re-render) and cart-drawer (auto-open) via ThemeEvents.cartUpdate.
      this.dispatchEvent(
        new CartAddEvent(response, this.id, {
          source: 'shop-the-look',
          sections: response.sections,
        })
      );

      this.#reset();
      /** @type {any} */ (this.closest('dialog-component'))?.closeDialog?.();
    } catch (error) {
      console.error(error);
      submit.disabled = false;
    }
  }

  /** Clears selections and unchecks the items so the drawer is fresh when reopened. */
  #reset() {
    this.#selections.clear();
    for (const checkbox of this.querySelectorAll('.checkbox__input')) {
      if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
    }
    this.#updateSubmit();
  }
}

if (!customElements.get('shop-the-look-component')) {
  customElements.define('shop-the-look-component', ShopTheLookComponent);
}
